import { Component, computed, effect, input, output, viewChild } from '@angular/core';
import { AgGridComponent, FieldTypes } from '@digiwall/ag-grid';
import { ColDef, GridOptions, IRowNode, SelectionChangedEvent } from '@ag-grid-community/core';
import { LocalizationModule, LocalizationService } from '@abp/ng.core';
import { EligibleDeliveryNoteForPreparationDto } from '@digiwall/goliath-documents/proxy';

@Component({
  selector: 'document-eligible-delivery-notes-grid',
  templateUrl: './eligible-delivery-notes-grid.component.html',
  styleUrl: './eligible-delivery-notes-grid.component.scss',
  imports: [AgGridComponent, LocalizationModule],
  standalone: true,
})
export class EligibleDeliveryNotesGridComponent {
  /** `null` while the caller is still fetching: neither the grid nor the empty state is shown yet. */
  readonly deliveryNotes = input<EligibleDeliveryNoteForPreparationDto[] | null>(null);
  readonly currentDeliveryNoteId = input<string | undefined>();
  readonly selectionChange = output<EligibleDeliveryNoteForPreparationDto[]>();

  protected readonly hasDeliveryNotes = computed(() => (this.deliveryNotes()?.length ?? 0) > 0);
  protected readonly hasNoDeliveryNotes = computed(() => this.deliveryNotes()?.length === 0);

  protected readonly agGrid = viewChild<AgGridComponent>('agGrid');

  protected columnDefs: ColDef[] = [];

  /**
   * The selection is read straight from the grid api rather than from the AgGridComponent
   * `selectedRows` model: that model is maintained by toggling on every row-selected event, so
   * re-asserting a selection programmatically (see below) would add the same note twice and
   * duplicate its lines in the preview.
   */
  protected readonly gridOptions: GridOptions = {
    onSelectionChanged: event => this.onGridSelectionChanged(event),
  };

  constructor(private readonly localizationService: LocalizationService) {
    this.setupColumnDefinitions();

    effect(() => {
      const notes = this.deliveryNotes();
      const currentId = this.currentDeliveryNoteId();
      if (notes?.length && currentId) {
        setTimeout(() => this.selectCurrentNote());
      }
    });
  }

  private onGridSelectionChanged(event: SelectionChangedEvent): void {
    // The delivery note the dialog was opened from always takes part in the preparation. Putting
    // it back through the grid keeps the grid the single source of truth; the resulting event
    // finds it selected and falls through to the emit below.
    const currentNode = this.findCurrentNote();
    if (currentNode && !currentNode.isSelected()) {
      currentNode.setSelected(true);
      return;
    }

    this.selectionChange.emit(event.api.getSelectedNodes().map(node => node.data));
  }

  private selectCurrentNote(): void {
    const currentNode = this.findCurrentNote();
    if (!currentNode || currentNode.isSelected()) return;

    currentNode.setSelected(true);
    this.agGrid()!.grid.api.ensureNodeVisible(currentNode, 'middle');
  }

  private findCurrentNote(): IRowNode | undefined {
    const currentId = this.currentDeliveryNoteId();
    if (!currentId) return undefined;

    // The grid keys its rows on data.id, so the note id is the row id.
    return this.agGrid()?.grid?.api?.getRowNode(currentId);
  }

  private setupColumnDefinitions() {
    this.columnDefs = [
      {
        headerName: this.localizationService.instant('Doc::CustomerDeliveryNote:DisplayNumber'),
        field: 'displayNumber',
        width: 150,
        pinned: 'left',
        filter: false,
        sort: 'desc',
      },
      {
        headerName: this.localizationService.instant('Doc::OrderDate'),
        field: 'date',
        width: 120,
        filter: false,
        valueFormatter: params => {
          if (params.value) {
            return new Date(params.value).toLocaleDateString();
          }
          return '';
        },
      },
      {
        headerName: this.localizationService.instant('Doc::CustomerReference'),
        field: 'reference',
        width: 150,
        filter: false,
      },
      {
        headerName: this.localizationService.instant('Contact::ThirdParty:Name'),
        field: 'thirdParty',
        width: 200,
        flex: 1,
        filter: false,
      },
      {
        headerName: this.localizationService.instant('Doc::CustomerAddress'),
        field: 'deliveryAddress',
        width: 250,
        flex: 1,
        filter: false,
      },
      {
        headerName: this.localizationService.instant('Doc::TotalAmount'),
        field: 'totalAmountExclTax',
        width: 150,
        type: FieldTypes.Currency,
        filter: false,
      },
      {
        headerName: this.localizationService.instant('Doc::RemainingAmount'),
        field: 'remainingAmountToPrepareExclTax',
        width: 180,
        type: FieldTypes.Currency,
        filter: false,
        cellStyle: params => {
          if (params.value > 0) {
            return { color: '#d73527', fontWeight: 'bold' };
          }
          return null;
        },
      },
    ];
  }

  protected onGridReady() {
    const grid = this.agGrid();
    if (grid) grid.hidePanelColumn = true;
  }
}
